import React from 'react'

/**
 * A horizontal rule + title + optional description, used to group
 * content on the profile page. Looks like an editorial section break.
 *
 * Visual rules:
 *   - The title is set in Source Serif 4 (h2/h3).
 *   - A 1px hair-line `border-rule` separates sections vertically.
 *   - `emphasis` lifts the section's importance: it gets a thicker
 *     border-left signal stripe (used for "Reelle ejere" +
 *     "Seneste ændringer" on the profile page).
 *   - `compact` tightens padding, title size, and list row rhythm for
 *     dense profile tabs.
 */
interface SectionProps {
  title: string
  description?: React.ReactNode
  emphasis?: boolean
  trailing?: React.ReactNode
  children: React.ReactNode
  className?: string
  id?: string
  /** Tighter vertical rhythm (profile tabs, long lists). */
  compact?: boolean
  /** Top hair-line between sections (default true). */
  borderTop?: boolean
}

const Section: React.FC<SectionProps> = ({
  title,
  description,
  emphasis = false,
  trailing,
  children,
  className = '',
  id,
  compact = false,
  borderTop = true,
}) => {
  const blockPad = compact ? 'pt-4 pb-4' : 'pt-6 pb-8'
  const rail = emphasis
    ? compact
      ? 'border-l-2 border-l-signal pl-3 sm:pl-4 -ml-3 sm:-ml-4'
      : 'border-l-2 border-l-signal pl-4 sm:pl-6 -ml-4 sm:-ml-6'
    : ''
  const headerMb = compact ? 'mb-2' : 'mb-4'
  const titleClass = compact ? 'font-serif text-xl text-ink' : 'font-serif text-2xl text-ink'
  const descClass = compact
    ? 'mt-0.5 text-xs text-ink-500 max-w-prose leading-snug'
    : 'mt-1 text-sm text-ink-500 max-w-prose'

  return (
    <section
      id={id}
      className={`${blockPad} ${borderTop ? 'border-t border-rule' : ''} ${rail} ${className}`}
    >
      <header className={`flex flex-wrap items-center justify-between gap-x-6 gap-y-2 ${headerMb}`}>
        <div className="min-w-0">
          <h2 className={titleClass}>{title}</h2>
          {description && <p className={descClass}>{description}</p>}
        </div>
        {trailing && <div className="shrink-0 ml-auto">{trailing}</div>}
      </header>
      {compact ? <div className="[&_li]:py-2">{children}</div> : children}
    </section>
  )
}

export default Section
